import { axios } from '../utilities';
import { PAGINATION } from '../../../shared/constants';
import { getDateFromStartInIsoString } from '../helpers/timeline';

const getProjectById = (
  projectId,
  specifiedProjectFields = [],
) => axios.get(
  `api/projects/${projectId}`,
  { params: { specifiedProjectFields } },
);

const getPackageByProjectId = (
  projectId,
  specifiedPackageFields = [],
) => axios.get(
  `api/projects/${projectId}/package`,
  { params: { specifiedPackageFields } },
);

const getChaptersAndResourcesByProjectId = (projectId, permissions) => axios.get(
  `api/projects/${projectId}/books/chapters`,
  { params: permissions },
);

const getBookSampleAndResourcesByProjectId = (projectId) => axios.get(`api/projects/${projectId}/books/book-sample`);

const getChaptersAndResourcesByUser = permissions => axios.get(
  'api/projects/books/chapters',
  { params: permissions },
);

const getBookSampleAndResourcesByUser = () => axios.get('api/projects/books/book-sample');

const createProject = (
  project,
  customer,
  writer,
  editor,
  developmentalEditor,
  proProofreader,
  contentTeam,
  packageDoc,
  timelineSchedule,
  extensionFeeDeadline,
  isProjectTimelineShown,
  isProjectAccessBlocked,
  isEmailShouldBeSentOnBlock,
  salesOwner,
  customUpgradeRequest,
  printerType,
) => axios.post(
  'api/projects',
  {
    data: {
      project,
      customer,
      writer,
      editor,
      developmentalEditor,
      proProofreader,
      contentTeam,
      package: packageDoc,
      timelineSchedule,
      extensionFeeDeadline,
      isProjectTimelineShown,
      isProjectAccessBlocked,
      isEmailShouldBeSentOnBlock,
      salesOwner,
      customUpgradeRequest,
      printerType,
    },
  },
);

const editProjectWithRelatedData = (
  customer,
  writer,
  editor,
  developmentalEditor,
  proProofreader,
  contentTeam,
  packageDoc,
  project,
  timelineSchedule,
  extensionFeeDeadline,
  isProjectTimelineShown,
  isProjectAccessBlocked,
  isEmailShouldBeSentOnBlock,
  salesOwner,
  customUpgradeRequest,
  printerType,
) => axios.put(
  `api/projects/${project._id}/edit-with-related-data`,
  {
    data: {
      customer,
      writer,
      editor,
      developmentalEditor,
      proProofreader,
      contentTeam,
      package: packageDoc,
      project,
      timelineSchedule,
      extensionFeeDeadline,
      isProjectTimelineShown,
      isProjectAccessBlocked,
      isEmailShouldBeSentOnBlock,
      salesOwner,
      customUpgradeRequest,
      printerType,
    },
  },
);

const deleteProjectById = projectId => axios.delete(`api/projects/${projectId}`);

const toggleProjectArchivingById = (projectId, isProjectCompleted) => axios.put(`api/projects/${projectId}/toggle-archiving`, { isProjectCompleted });

const getDataToCreateProject = () => axios.get('api/projects/data-to-create');

const getDataToEditProject = (projectId) => axios.get(`api/projects/${projectId}/data-to-edit`);

const getDataForProjectsList = (
  {
    projectStatuses,
    projectStages,
    projectTypes,
    packageTypes,
    customerNameOrEmail,
    writerNameOrEmail,
    editorNameOrEmail,
    hubspotDealId,
    customerNotRegistered,
    firstPhotoUploaded,
    notStartedUploadingPhotos,
    photosSubmitted,
    coverNotApproved,
    interviewsScheduled,
    interviewsNotScheduled,
    writerRated,
    writerNotRated,
    pdfGenerated,
    proofSendToCustomer,
    timelineShown,
    deadlineInNextWeek,
    deadlineOverdue,
    projectTimelineUpdated,
    createFromDate,
    createToDate,
    updateFromDate,
    updateToDate,
  } = {},
  { pageIndex, pageSize } = { pageIndex: PAGINATION.pageIndex, pageSize: PAGINATION.pageSize },
  sortBy = null,
) => axios.get(
  '/api/projects/data-for-list',
  {
    params: {
      filters: {
        projectStatuses,
        projectStages,
        projectTypes,
        packageTypes,
        customerNameOrEmail,
        writerNameOrEmail,
        editorNameOrEmail,
        hubspotDealId,
        customerNotRegistered,
        firstPhotoUploaded,
        notStartedUploadingPhotos,
        photosSubmitted,
        coverNotApproved,
        interviewsScheduled,
        interviewsNotScheduled,
        writerRated,
        writerNotRated,
        pdfGenerated,
        proofSendToCustomer,
        timelineShown,
        deadlineInNextWeek,
        deadlineOverdue,
        projectTimelineUpdated,
        createFromDate,
        createToDate,
        updateFromDate,
        updateToDate,
      },
      pagination: { pageIndex, pageSize },
      sortBy,
      userCurrentDate: getDateFromStartInIsoString(new Date()),
    },
  },
);


const getProjectOverviewDataByProjectId = projectId => axios.get(`api/projects/${projectId}/overview-data-by-project`);
const getProjectOverviewDataByCustomerId = customerId => axios.get(`api/projects/${customerId}/overview-data-by-customer`);

const getWritersMatch = (
  projectId,
  { pageIndex, pageSize } = { pageIndex: PAGINATION.pageIndex, pageSize: PAGINATION.pageSize },
  sortBy = null,
  {
    nameOrEmail,
    levels,
    distances,
    genders,
    keywords,
    otherLanguages,
    otherInformation,
    writingStyle,
    comfortLevel,
    statuses,
    accreditedOnly,
    dbsScreenedOnly,
    newWritersOnly,
    createFromDate,
    createToDate,
    lastLoggedInFromDate,
    lastLoggedInToDate,
  } = {},
) => axios.get(
  `/api/projects/${projectId}/users/writer-match`,
  {
    params: {
      pagination: { pageIndex, pageSize },
      sortBy,
      filters: {
        nameOrEmail,
        levels,
        distances,
        genders,
        keywords,
        otherLanguages,
        otherInformation,
        writingStyle,
        comfortLevel,
        statuses,
        accreditedOnly,
        dbsScreenedOnly,
        newWritersOnly,
        createFromDate,
        createToDate,
        lastLoggedInFromDate,
        lastLoggedInToDate,
      },
    },
  },
);

const getExcelWritersMatch = (
  projectId,
  sortBy = null,
  filters = {},
) => axios.get(
  `/api/projects/${projectId}/users/excel-writer-match`,
  { params: { sortBy, filters } },
);

const addWriterToShortlist = (
  projectId,
  writerId,
) => axios.post(
  `/api/projects/${projectId}/writer-match/add-writer-to-shortlist`,
  { writerId },
);

const removeWriterFromShortlist = (
  projectId,
  writerId,
) => axios.put(
  `/api/projects/${projectId}/writer-match/remove-writer-from-shortlist`,
  { writerId },
);

const addWriterToProject = (projectId, writerId, writerLevelForProject) => axios.put(
  `/api/projects/${projectId}/add-writer`,
  { writerId, writerLevelForProject },
);

const moveToNextStageOfProject = (projectId, isEmailsIncluded, bookFieldsToUpdate) =>
  axios.put(`/api/projects/${projectId}/skip-project-stage`, { isEmailsIncluded, bookFieldsToUpdate });

const getTimelineDataForCustomer = customerId => axios.get(`api/projects/${customerId}/timeline-data-by-customer`);

const toggleUserEnableToEditDraft = (projectId, userRole) => axios.put(`api/projects/${projectId}/toggle-user-edit-draft`, { userRole });

const toggleUserEnableToEditOutline = (projectId, userRole) => (
  axios.put(`api/projects/${projectId}/toggle-user-edit-outline`, { userRole })
);

const toggleUserEnableToDownloadOutline = (projectId, userRole) => (
  axios.put(`api/projects/${projectId}/toggle-user-download-outline`, { userRole })
);

const getProjectsDataForExcel = (filters, sortBy = null) => axios.get(
  '/api/projects/data-for-excel',
  { params: { filters, sortBy, userCurrentDate: getDateFromStartInIsoString(new Date()) } },
);

const customerReviewedUpgrades = () => axios.post('api/projects/customer-reviewed-upgrades');

const getDataFromHubspot = hubspotDealID => axios.get(`/api/projects/data-from-hubspot/${hubspotDealID}`);

const editorSeenRequestForUpgrade = projectId => axios.put(`/api/projects/${projectId}/editor-seen-upgrade-request`);

const getProjectAndBookData = (projectId) => (
  axios.get(`/api/projects/${projectId}/project-and-book-data`)
);

const cancelUpgradeRequest = (projectId, upgradeId) => (
  axios.put(`/api/projects/${projectId}/upgrade-request/${upgradeId}/cancel`)
);

const completeUpgradeRequest = (projectId, upgradeId) => (
  axios.put(`/api/projects/${projectId}/upgrade-request/${upgradeId}/complete`)
);

const getNextStageForProject = (projectId) => axios.get(`api/projects/${projectId}/next-stage`);

export default {
  getProjectOverviewDataByProjectId,
  getProjectOverviewDataByCustomerId,
  getProjectById,
  getPackageByProjectId,
  createProject,
  editProjectWithRelatedData,
  deleteProjectById,
  toggleProjectArchivingById,
  getChaptersAndResourcesByProjectId,
  getChaptersAndResourcesByUser,
  getDataToCreateProject,
  getDataToEditProject,
  getDataForProjectsList,
  getWritersMatch,
  addWriterToShortlist,
  removeWriterFromShortlist,
  addWriterToProject,
  moveToNextStageOfProject,
  getTimelineDataForCustomer,
  toggleUserEnableToEditDraft,
  toggleUserEnableToEditOutline,
  toggleUserEnableToDownloadOutline,
  getProjectsDataForExcel,
  getExcelWritersMatch,
  customerReviewedUpgrades,
  getDataFromHubspot,
  editorSeenRequestForUpgrade,
  getBookSampleAndResourcesByUser,
  getBookSampleAndResourcesByProjectId,
  getProjectAndBookData,
  cancelUpgradeRequest,
  completeUpgradeRequest,
  getNextStageForProject,
};
